import { Zone as InspectionZone, WorkflowStages } from '@/__generated__/graphql';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useFeatureFlag } from '@/hooks';
import { useDraggable } from '@dnd-kit/core';
import { Stack, Tooltip } from '@mui/material';
import { MouseEvent } from 'react';
import { DifficultySelector } from './DifficultySelector';
import { dragAndDropStyle, Name, ZoneCard } from './styles';
import { ZoneMenu } from './ZoneMenu';
import { useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/state';
import { AssigneeAvatar } from '@/components/ZonesManagementRevamped/AssigneeAvatar';
import { abyssColors } from '@/theme/colors';

type zoneProps = {
  data: InspectionZone;
  selectedZones: InspectionZone[];
  details?: WorkflowStages;
  setSelectedZones: (val: InspectionZone[]) => void;
};

export const Zone = ({ data, selectedZones, details, setSelectedZones }: zoneProps) => {
  const zones = useAtomValue(state.zones);
  const setShowZoneDetailModal = useSetAtom(state.showZoneDetailModal);
  const setSelectedZoneDetails = useSetAtom(state.selectedZoneDetails);
  const isAitTaskLead = useFeatureFlag('ait-task-lead');
  const isSelected = selectedZones.some((zone) => zone.id === data.id);
  const assignedUser = data?.workflowStatus?.assignedToUser;
  const picture = assignedUser?.picture;
  const name = assignedUser?.name;

  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: data.id || 'unknown-zone',
    data: {
      zone: data,
      isSelected,
    },
  });

  const handleZoneClick = (e: MouseEvent) => {
    if (e.ctrlKey) {
      e.stopPropagation();
      setSelectedZones(
        isSelected ? selectedZones.filter((zone) => zone.id !== data.id) : [...selectedZones, data]
      );
      return;
    }
    if (!isDragging && !isSelected) {
      e.preventDefault();
      const zone = zones.find((zone) => zone.id === data.id);
      if (zone) {
        setSelectedZoneDetails(zone);
      }
      // disabled for now
      setShowZoneDetailModal(false);
    }
  };

  const style = dragAndDropStyle({ transform, isDragging, isSelected });
  return (
    <>
      <ZoneCard
        ref={setNodeRef}
        style={style}
        onClick={handleZoneClick}
        {...(isAitTaskLead ? listeners : {})}
      >
        <Tooltip
          title={isDragging ? '' : isAitTaskLead ? 'Ctrl + Click to select multiple zones' : ''}
          placement="top"
          arrow
        >
          <Stack
            direction="row"
            gap="0.8rem"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Name>{data.name ?? NOT_APPLICABLE_LABEL}</Name>
            <ZoneMenu zoneId={data.id} />
          </Stack>
        </Tooltip>
        <AssigneeAvatar
          zoneId={data.id}
          sx={{ width: '140px', height: '32px' }}
          details={details}
          assigneeName={name ?? undefined}
          profileImage={picture ?? undefined}
          assingeFieldColor={abyssColors.primary[300]}
        />
        <DifficultySelector
          difficulty={data?.workflowStatus?.difficultyLevel ?? NOT_APPLICABLE_LABEL}
          zoneId={data.id}
        />
      </ZoneCard>
    </>
  );
};
